import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';

/**
 * Data contract for {@link QuotaUpgradeDialogComponent}. Built from a `QuotaExceededError`
 * body by the {@link QuotaPromptService}; the label/unit-aware formatting is done by the caller
 * so this dialog stays presentation-only.
 */
export interface QuotaUpgradeDialogData {
    /** Human label for the entitlement, e.g. "Connections". */
    readonly label: string;
    /** Pre-formatted current value, e.g. "5" or "2 GiB". */
    readonly current: string;
    /** Pre-formatted limit value, e.g. "5" or "2 GiB". */
    readonly limit: string;
    /** Material Symbols icon for the entitlement. */
    readonly icon: string;
}

/**
 * The upgrade prompt shown when a create action is rejected for hitting a tenant quota
 * (HTTP 403 `QUOTA_EXCEEDED`). It is deliberately a modal — not a transient toast — because
 * the action failed and the user needs a clear next step (review usage / upgrade), not a
 * message that vanishes in 5s. Opened centrally by {@link QuotaPromptService} from the
 * quota-error interceptor, so every create flow gets this without per-call wiring.
 */
@Component({
    selector: 'app-quota-upgrade-dialog',
    standalone: true,
    imports: [MatDialogModule, MatButtonModule, MatIconModule],
    template: `
        <h2 mat-dialog-title>
            <mat-icon fontSet="material-symbols-outlined">{{ data.icon }}</mat-icon>
            You've reached your {{ data.label }} limit
        </h2>
        <mat-dialog-content>
            <p class="message">
                You're using <strong>{{ data.current }}</strong> of your
                <strong>{{ data.limit }}</strong> {{ data.label }} allowance. Remove an existing
                one, or upgrade your plan to add more.
            </p>
            <div class="usage-pill" role="status" aria-live="polite">
                <span class="usage-label">{{ data.label }}</span>
                <span class="usage-figure">{{ data.current }} / {{ data.limit }}</span>
            </div>
        </mat-dialog-content>
        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close>Not now</button>
            <button mat-stroked-button (click)="viewUsage()">View usage</button>
            <button mat-flat-button color="primary" (click)="upgrade()">
                <mat-icon fontSet="material-symbols-outlined">upgrade</mat-icon>
                Upgrade plan
            </button>
        </mat-dialog-actions>
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: [`
        h2[mat-dialog-title] {
            display: flex;
            align-items: center;
            gap: var(--space-2);

            mat-icon { color: var(--primary-600); }
        }

        .message {
            color: var(--neutral-700);
            font-size: var(--text-sm);
            line-height: 1.6;
            margin: 0 0 var(--space-4);

            strong { color: var(--neutral-900); }
        }

        .usage-pill {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--space-4);
            padding: var(--space-3) var(--space-4);
            border: var(--border-width) solid var(--warning-200);
            background: var(--warning-50);
            border-radius: var(--radius);

            .usage-label {
                font-size: var(--text-sm);
                font-weight: var(--font-medium);
                color: var(--warning-700);
            }

            .usage-figure {
                font-size: var(--text-sm);
                font-weight: var(--font-semibold);
                color: var(--warning-900);
                font-variant-numeric: tabular-nums;
            }
        }
    `]
})
export class QuotaUpgradeDialogComponent {
    data = inject<QuotaUpgradeDialogData>(MAT_DIALOG_DATA);
    private dialogRef = inject(MatDialogRef<QuotaUpgradeDialogComponent>);
    private router = inject(Router);

    viewUsage(): void {
        this.dialogRef.close();
        this.router.navigate(['/admin/billing']);
    }

    upgrade(): void {
        this.dialogRef.close();
        // Manual-invoicing for launch: send the user to billing where the plan/usage lives.
        // When self-serve upgrade ships, point this at the upgrade flow instead.
        this.router.navigate(['/admin/billing']);
    }
}
